import { DependencyList, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useLoggedEffect = (callback: () => void, dependencies: DependencyList) => {
  const previousDependencies = useRef<DependencyList>([]);

  useEffect(() => {
    if (previousDependencies.current.length) {
      dependencies.forEach((dep, i) => {
        if (dep !== previousDependencies.current[i]) {
          console.log(`Dependency at index ${i} changed:`, dep);
        }
      });
    }

    previousDependencies.current = dependencies;
    return callback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};

export const useLoggedMemo = <T>(factory: () => T, dependencies: DependencyList): T => {
  const previousDependencies = useRef<DependencyList>([]);

  const memoizedValue = useMemo(() => {
    if (previousDependencies.current.length) {
      dependencies.forEach((dep, i) => {
        if (dep !== previousDependencies.current[i]) {
          console.log(`Dependency at index ${i} changed:`, dep);
        }
      });
    }

    previousDependencies.current = dependencies;
    return factory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return memoizedValue;
};

export const useLoggedCallback = <T extends (...args: any[]) => any>(callback: T, dependencies: DependencyList): T => {
  const previousDependencies = useRef<DependencyList>([]);

  const memoizedCallback = useMemo(() => {
    if (previousDependencies.current.length) {
      dependencies.forEach((dep, i) => {
        if (dep !== previousDependencies.current[i]) {
          console.log(`LOGGED_CALLBACK: Dependency at index ${i} changed:`, dep);
        }
      });
    }

    previousDependencies.current = dependencies;
    return callback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return memoizedCallback;
};

// great for following the stack trace
export const useLoggedState = <T>(initialValue: T): [T, (value: SetStateAction<T>) => void] => {
  const [state, setState] = useState<T>(initialValue);

  const setLoggedState = useCallback((value: SetStateAction<T>) => {
    console.log('State changed:', value);
    return setState(value);
  }, []);

  return [state, setLoggedState];
};
